import { useQuery } from '@tanstack/react-query'
import { Gift, Undo2 } from 'lucide-react'
import { useState } from 'react'

import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { formatDate } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { adminApi } from './api'
import { GrantPlanDialog, RevokeGrantDialog } from './GrantPlanDialog'
import { Pager, PlanBadge, SearchInput, Seats, SubscriptionBadge, TableScroll, Td, Th } from './parts'
import type { AdminWorkspace } from './types'

/**
 * `/admin/workspaces` — every workspace on the deployment.
 *
 * This is the screen the console exists for: the owner's question was "I can't
 * upgrade for some users", and the answer is a row per workspace with a Grant
 * plan action on it.
 *
 * The columns are chosen to make one judgement possible at a glance — is this
 * workspace paying, comped, or free, and does its plan match its size. Hence
 * plan-with-source next to seats-used-of-limit: a Free workspace with nine
 * members is a conversation, and a Business workspace with one is a different
 * one.
 */
export default function AdminWorkspacesPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [granting, setGranting] = useState<AdminWorkspace | null>(null)
  const [revoking, setRevoking] = useState<AdminWorkspace | null>(null)

  const workspaces = useQuery({
    queryKey: queryKeys.adminWorkspaces(search, page),
    queryFn: ({ signal }) => adminApi.workspaces({ q: search || undefined, page }, { signal }),
    // Keeps the previous page on screen while the next one loads, so paging and
    // typing do not blink the table out of existence.
    placeholderData: (previous) => previous,
  })

  function changeSearch(next: string) {
    setSearch(next)
    setPage(1)
  }

  const rows = workspaces.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Workspaces"
        description="Every workspace on this deployment, with the plan it is on and where that plan came from."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={changeSearch}
          label="Search workspaces"
          placeholder="Search by name, slug or owner email"
        />
      </div>

      {workspaces.isError ? (
        <Alert tone="danger">{messageFor(workspaces.error)}</Alert>
      ) : (
        <Card>
          {workspaces.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title={search ? 'No workspaces match that search' : 'No workspaces yet'}
              description={
                search
                  ? 'Search runs over the workspace name, its slug and its owner’s name and email.'
                  : undefined
              }
              action={search ? <Button onClick={() => changeSearch('')}>Clear search</Button> : undefined}
            />
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <Th>Workspace</Th>
                  <Th>Owner</Th>
                  <Th>Plan</Th>
                  <Th numeric>Seats</Th>
                  <Th numeric>Projects</Th>
                  <Th numeric>Issues</Th>
                  <Th>Subscription</Th>
                  <Th>Created</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((workspace) => (
                  <tr key={workspace.id} className="transition hover:bg-ink-50">
                    <Td>
                      <span className="block font-medium">{workspace.name}</span>
                      <span className={cn('block text-xs', mutedTextClass)}>
                        {workspace.slug} · {workspace.type}
                      </span>
                    </Td>
                    <Td>
                      {workspace.owner ? (
                        <>
                          <span className="block">{workspace.owner.name}</span>
                          <span className={cn('block text-xs', mutedTextClass)}>
                            {workspace.owner.email}
                          </span>
                        </>
                      ) : (
                        <span className={mutedTextClass}>—</span>
                      )}
                    </Td>
                    <Td>
                      <PlanBadge workspace={workspace} />
                      {workspace.grant ? (
                        <span className={cn('mt-0.5 block text-xs', mutedTextClass)}>
                          {workspace.grant.pro_until
                            ? `Until ${formatDate(workspace.grant.pro_until)}`
                            : 'No expiry'}
                          {workspace.grant.reason ? ` · ${workspace.grant.reason}` : ''}
                        </span>
                      ) : null}
                    </Td>
                    <Td numeric>
                      <Seats used={workspace.member_count} limit={workspace.seat_limit} />
                    </Td>
                    <Td numeric>{workspace.project_count}</Td>
                    <Td numeric>{workspace.issue_count}</Td>
                    <Td>
                      <SubscriptionBadge status={workspace.subscription_status} />
                    </Td>
                    <Td>
                      <span className="whitespace-nowrap tabular-nums">
                        {formatDate(workspace.created_at)}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" onClick={() => setGranting(workspace)}>
                          <Gift className="size-3.5" aria-hidden="true" />
                          Grant plan
                        </Button>
                        {workspace.grant ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setRevoking(workspace)}
                          >
                            <Undo2 className="size-3.5" aria-hidden="true" />
                            Revoke
                          </Button>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}

          {workspaces.data ? (
            <Pager
              page={workspaces.data.page}
              perPage={workspaces.data.per_page}
              total={workspaces.data.total}
              onPage={setPage}
              noun="workspaces"
            />
          ) : null}
        </Card>
      )}

      <GrantPlanDialog
        workspace={granting}
        open={granting !== null}
        onClose={() => setGranting(null)}
      />
      <RevokeGrantDialog
        workspace={revoking}
        open={revoking !== null}
        onClose={() => setRevoking(null)}
      />
    </>
  )
}
